import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "../api/axiosInstance";
import {
  readStoredDisplayCurrency,
  storeDisplayCurrency,
} from "./currencyStorage";
import {
  formatCurrencyAmount,
  getCurrencyMinorUnits,
} from "./currencyPrecision";
import { readStoredAuthToken, readStoredUser } from "../utils/sessionUser";

const CurrencyDisplayContext = createContext(null);

function readActiveBusinessUnitId(locationSearch) {
  const fromUrl = Number(new URLSearchParams(locationSearch).get("business_unit_id") || 0);
  if (fromUrl) return fromUrl;
  const user = readStoredUser();
  return Number(user?.active_business_unit_id || 0) || null;
}

export function CurrencyDisplayProvider({ children }) {
  const location = useLocation();
  const businessUnitId = readActiveBusinessUnitId(location.search);
  const [configuration, setConfiguration] = useState(null);
  const [displayCurrency, setDisplayCurrencyState] = useState("");
  const [loading, setLoading] = useState(false);

  const refreshConfiguration = useCallback(async () => {
    if (!businessUnitId || !readStoredAuthToken()) {
      setConfiguration(null);
      setDisplayCurrencyState("");
      return null;
    }

    setLoading(true);
    try {
      const response = await axios.get("/api/currency-settings", {
        params: { business_unit_id: businessUnitId },
      });
      const next = response.data || null;
      setConfiguration(next);
      const stored = readStoredDisplayCurrency(businessUnitId);
      const available = Array.isArray(next?.currencies) ? next.currencies : [];
      const selected = available.includes(stored)
        ? stored
        : next?.base_currency_code || "";
      setDisplayCurrencyState(selected);
      storeDisplayCurrency(businessUnitId, selected, next?.current_rate_set_id);
      return next;
    } catch (error) {
      console.warn("[CurrencyDisplay] Unable to load currency settings:", error);
      setConfiguration(null);
      setDisplayCurrencyState("");
      return null;
    } finally {
      setLoading(false);
    }
  }, [businessUnitId]);

  useEffect(() => {
    refreshConfiguration();
  }, [refreshConfiguration]);

  useEffect(() => {
    const handleRefresh = () => refreshConfiguration();
    window.addEventListener("pilcore-currency-settings-updated", handleRefresh);
    return () => window.removeEventListener("pilcore-currency-settings-updated", handleRefresh);
  }, [refreshConfiguration]);

  const setDisplayCurrency = useCallback(
    (currencyCode) => {
      const code = String(currencyCode || "").toUpperCase();
      if (!configuration?.enabled || !configuration?.currencies?.includes(code)) return;
      setDisplayCurrencyState(code);
      storeDisplayCurrency(businessUnitId, code, configuration.current_rate_set_id);
      window.dispatchEvent(
        new CustomEvent("pilcore-display-currency-changed", {
          detail: { business_unit_id: businessUnitId, currency: code },
        })
      );
    },
    [businessUnitId, configuration]
  );

  const convertFromBase = useCallback(
    (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return null;
      if (!configuration?.enabled || !displayCurrency) return numeric;
      const rate = Number(configuration?.rates?.[displayCurrency]);
      if (!Number.isFinite(rate) || rate <= 0) return numeric;
      return numeric / rate;
    },
    [configuration, displayCurrency]
  );

  const currencyCatalog = useMemo(
    () => Array.isArray(configuration?.available_currencies)
      ? configuration.available_currencies
      : [],
    [configuration?.available_currencies]
  );

  const getMinorUnits = useCallback(
    (currencyCode) => getCurrencyMinorUnits(currencyCode, currencyCatalog),
    [currencyCatalog]
  );

  const formatFromBase = useCallback(
    (value, options = {}) => {
      const converted = convertFromBase(value);
      if (converted == null) return "—";
      const currency = displayCurrency || configuration?.base_currency_code || "EUR";
      return formatCurrencyAmount(converted, currency, {
        ...options,
        catalog: options.catalog || currencyCatalog,
      });
    },
    [configuration?.base_currency_code, convertFromBase, currencyCatalog, displayCurrency]
  );

  const value = useMemo(
    () => ({
      businessUnitId,
      configuration,
      adminEnabled: Boolean(configuration?.admin_enabled),
      enabled: Boolean(configuration?.enabled),
      loading,
      baseCurrency: configuration?.base_currency_code || "EUR",
      displayCurrency: displayCurrency || configuration?.base_currency_code || "EUR",
      currencies: configuration?.currencies || [],
      currencyCatalog,
      minorUnits: getMinorUnits(displayCurrency || configuration?.base_currency_code || "EUR"),
      rateSetId: configuration?.current_rate_set_id || null,
      rates: configuration?.rates || {},
      getMinorUnits,
      setDisplayCurrency,
      convertFromBase,
      formatFromBase,
      refreshConfiguration,
    }),
    [
      businessUnitId,
      configuration,
      convertFromBase,
      currencyCatalog,
      displayCurrency,
      formatFromBase,
      getMinorUnits,
      loading,
      refreshConfiguration,
      setDisplayCurrency,
    ]
  );

  return (
    <CurrencyDisplayContext.Provider value={value}>
      {children}
    </CurrencyDisplayContext.Provider>
  );
}

export function useCurrencyDisplay() {
  const context = useContext(CurrencyDisplayContext);
  if (!context) throw new Error("useCurrencyDisplay must be used inside CurrencyDisplayProvider");
  return context;
}
